/* Admin: Packer Daily Count — parcels photographed per packer on a day + packed parcels of that day still missing photos. */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, ChevronDown, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Input } from "@/components/ui/input";
import { PhotoMissingTag } from "@/components/PhotoReminders";

const istToday = () => new Date(Date.now() + 5.5 * 3600000).toISOString().slice(0, 10);

const People = ({ people }) => (
  <table className="w-full text-xs" data-testid="pkd-people">
    <thead><tr className="border-b text-left text-slate-500"><th className="py-1">Who</th><th className="text-right">Parcels</th><th className="text-right">Photos</th></tr></thead>
    <tbody>{people.map(p => (
      <tr key={p.user_id} className="border-b last:border-0" data-testid={`pkd-person-${p.user_id}`}>
        <td className="py-1">{p.name}{p.is_packer
          ? <span className="ml-1 rounded bg-teal-100 px-1 text-[9px] font-bold uppercase text-teal-800">Packing Team</span>
          : <span className="ml-1 text-[10px] text-slate-400">team</span>}</td>
        <td className="text-right font-semibold" data-testid={`pkd-count-${p.user_id}`}>{p.parcels}</td>
        <td className="text-right text-slate-500">{p.photos}</td>
      </tr>))}
      {!people.length && <tr><td colSpan={3} className="py-3 text-center text-slate-400">No Packing Team users yet</td></tr>}</tbody>
  </table>
);

const Missing = ({ items, reload }) => (
  <div className="space-y-1" data-testid="pkd-missing">
    {items.map(x => (
      <div key={x.row_id} className="flex flex-wrap items-center gap-2 rounded border px-2 py-1 text-xs" data-testid={`pkd-missing-${x.row_id}`}>
        <b>{x.customer_name}</b><span className="text-slate-500">{x.code} · {x.mode} · {x.invoice_no}</span>
        <span className="ml-auto"><PhotoMissingTag r={{ id: x.row_id, customer_name: x.customer_name, photo_missing: x.missing }} onDone={reload} /></span>
      </div>))}
    {!items.length && <p className="py-2 text-center text-xs text-emerald-700" data-testid="pkd-missing-empty">Every packed partner parcel of this day has its photos.</p>}
  </div>
);

export const PackerDailyCard = () => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(istToday());
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { setD((await axios.get(`${API}/order-register/queries/packer-daily`, { headers: getAuthHeader(), params: { date } })).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load Packing Team count"); }
    setBusy(false);
  }, [date]);
  useEffect(() => { if (open) load(); }, [open, load]);
  return (
    <div className="rounded-lg border border-teal-200 bg-white p-3" data-testid="packer-daily-card">
      <button type="button" className="flex w-full items-center gap-2 text-left text-sm font-semibold text-teal-900" onClick={() => setOpen(v => !v)} data-testid="packer-daily-toggle">
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}<Camera className="h-4 w-4" /> Packing Team daily count
        {d && <span className="ml-1 text-xs font-normal text-slate-500">{d.total_parcels} parcels · {d.missing.length} missing photos</span>}
      </button>
      {open && (
        <div className="mt-2 space-y-3">
          <div className="flex items-center gap-2">
            <Input type="date" value={date} max={istToday()} onChange={(e) => setDate(e.target.value)} className="h-8 w-40 text-xs" data-testid="packer-daily-date" />
            <button type="button" onClick={load} className="rounded border p-1.5 text-slate-600" title="Refresh" data-testid="packer-daily-refresh">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}</button>
          </div>
          {d && <div className="grid gap-4 md:grid-cols-2">
            <div><p className="mb-1 text-xs font-semibold text-slate-600">Parcels photographed</p><People people={d.people} /></div>
            <div><p className="mb-1 text-xs font-semibold text-rose-700">Packed that day, photos still missing ({d.missing.length})</p><Missing items={d.missing} reload={load} /></div>
          </div>}
        </div>)}
    </div>
  );
};
